import { createAction } from 'redux-actions'
import { actionCreators as api } from 'reduxBits/api/actions'
import { setVerificationType } from 'reduxBits/account/actions'
import { trackingAdReplyFormError } from 'tracking/chat'
import log from 'sentry/log'
import { hasExpired } from 'components/pagesContent/Chat/utils/hasExpired'
import { SMS_AND_BANKID, BANKID, SMS } from 'constants/verificationTypes'

const prefix = 'Chat: '

export const SET_ERROR = `${prefix} Set error object`
export const setError = createAction(SET_ERROR)

export const CLEAR_ERROR = `${prefix} Clear error object`
export const clearError = createAction(CLEAR_ERROR)

export const SET_REMOVED_CHANNEL_URL = `${prefix} Set removed channel url`
export const setRemovedChannelUrl = createAction(SET_REMOVED_CHANNEL_URL)

export const IS_FETCHING_CREATE_CHANNEL = `${prefix} Is fetching create channel`
const isFetchingCreateChannel = createAction(IS_FETCHING_CREATE_CHANNEL)

export const SET_IS_BROWSING_FILES = `${prefix} Is browsing files on upload.`
export const setIsBrowsingFiles = createAction(SET_IS_BROWSING_FILES)

export const SET_CREATED_CHANNEL_URL = `${prefix} Set chat create channel url`
export const setCreatedChannelUrl = createAction(SET_CREATED_CHANNEL_URL)

export const SET_UNREAD_MESSAGES_COUNT = `${prefix} Set unread messages`
export const setUnreadMessageCount = createAction(SET_UNREAD_MESSAGES_COUNT)

export const SET_SENDBIRD_USER = `${prefix} Set sendbird user`
export const setSendbirdUser = createAction(SET_SENDBIRD_USER)

export const SET_SENDBIRD_USER_DISCONNECT = `${prefix} Set sendbird user disconnect`
export const setSendbirdUserDisconnect = createAction(SET_SENDBIRD_USER_DISCONNECT)

export const SET_WARNING_MODAL_TYPE = `${prefix} Set warning modal type`
export const setWarningModalType = createAction(SET_WARNING_MODAL_TYPE)

export const SET_CLIENT_SETTINGS = `${prefix} Set client settings`
export const setClientSettings = createAction(SET_CLIENT_SETTINGS)

export const ADD_RECIPIENT_MEMBER_BANKID_VERIFIED_CHANNEL_URLS = `${prefix} Add recipient BankId verified channels.`
export const addRecipientMemberBankidVerifiedChannelUrl = createAction(
  ADD_RECIPIENT_MEMBER_BANKID_VERIFIED_CHANNEL_URLS,
)

export const CLEAR_RECIPIENT_MEMBER_BANKID_VERIFIED_CHANNEL_URLS = `${prefix} Clear recipient BankId verified channels.`
export const clearRecipientMemberBankidVerifiedChannelUrls = createAction(
  CLEAR_RECIPIENT_MEMBER_BANKID_VERIFIED_CHANNEL_URLS,
)

export const SET_USER_SUSPENDED_TYPE = `${prefix} Set user suspended type`
export const setUserSuspendedType = createAction(SET_USER_SUSPENDED_TYPE)

/**
 * Handles Errors in UI and Sentry Logs.
 * @param {(Object|string)} error - On Sendbird error: https://sendbird.com/docs/chat/v3/dotnet/guides/error-codes
 * @param {string}          errorType - The types are specified in src/reduxBits/chat/reducer.js (SET_ERROR).
 * @param {Object[]}        sentryLog - Add Sentry logging.
 * @param {string}          sentryLog.description - Add Sentry logging message.
 */

export const errorHandler = ({ error, errorType, sentryLog = false }) => (dispatch) => {
  if (errorType) {
    dispatch(setError({ type: errorType, data: error }))
    if (sentryLog) {
      const logDesc = sentryLog.description ? `${sentryLog.description}, ` : ''
      log.message(`${logDesc} ${error}`)
    }
  }
}

export const getClientSettings = (retries = 0) => async (dispatch) => {
  const endpoint = 'messaging/client-settings/v1/content'
  try {
    const response = await dispatch(api.fetch(endpoint))
    if (response?.status === 200) {
      dispatch(setClientSettings(response.textOrJson))
    }
  } catch (error) {
    const maxRetries = 3
    if (retries < maxRetries) {
      return dispatch(getClientSettings(retries + 1))
    }
    log.message(`Fetch failed on ${endpoint}', ${error}`)
  }
}

export const fetchConnectProperties = (forceFetch = false) => async (dispatch, getState) => {
  const endpoint = 'messaging-sendbird-communication/v1/users/tokens'
  const { suspendedAccount: suspendedAccountFeature } = getState().optimizely.features
  // this timestamp refers to when the session was created
  const { timestamp: sessionCreated } = getState().user
  try {
    let connectProperties = JSON.parse(sessionStorage.getItem('chatConnectProperties'))

    if (
      !connectProperties?.verificationToken ||
      hasExpired(connectProperties?.expiresAt) ||
      connectProperties?.sessionCreated !== sessionCreated ||
      forceFetch
    ) {
      sessionStorage.removeItem('chatConnectProperties')
      const response = await dispatch(api.fetch(endpoint))
      connectProperties = { ...(await response?.textOrJson), sessionCreated }
      sessionStorage.setItem('chatConnectProperties', JSON.stringify(connectProperties))
      dispatch(setUserSuspendedType(''))
    }
    return connectProperties
  } catch (error) {
    const { content, response } = error

    if (
      suspendedAccountFeature?.enabled &&
      response?.status === 403 &&
      content?.error === '400301_account_suspended'
    ) {
      log.message(`SuspendedUser error: ${error}. Endpoint: ${endpoint}`)
      return dispatch(setUserSuspendedType(content.error))
    }

    dispatch(
      errorHandler({
        error,
        errorType: 'general',
        sentryLog: { description: '[fetchConnectProperties] failed.' },
      }),
    )
  }
}

export const removeConnectProperties = () => () => {
  let connectProperties = sessionStorage.getItem('chatConnectProperties')
  if (connectProperties) {
    sessionStorage.removeItem('chatConnectProperties')
  }
}

const trackError = (error, adId) => {
  trackingAdReplyFormError(adId, error)
}

export const createChannel = (adId, retries = 0) => async (dispatch, getState) => {
  const { suspendedAccount: suspendedAccountFeature } = getState().optimizely.features
  const { verificationToken } = await dispatch(fetchConnectProperties())
  const fetch = api.communicationApiFetch

  const endpoint = 'messaging-sendbird-communication/v3/channels'

  try {
    const response = await dispatch(
      fetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          ad_id: adId,
          verification_token: verificationToken,
        }),
      }),
    )

    if (response.channelUrl) {
      dispatch(setCreatedChannelUrl(response.channelUrl))
    } else {
      dispatch(
        errorHandler({
          error: response,
          errorType: 'general',
          sentryLog: { description: `Invalid response from ${endpoint}` },
        }),
      )
    }
  } catch (error) {
    const maxRetries = 1
    const { content, response } = error

    dispatch(isFetchingCreateChannel(false))

    if (response?.status === 403) {
      if (suspendedAccountFeature?.enabled && content?.error === '400301_account_suspended') {
        log.message(`SuspendedUser error: ${error}. Endpoint: ${endpoint}`)
        return dispatch(setUserSuspendedType(content.error))
      }

      if (content?.error === 'sms_or_bankid_verification_needed') {
        return dispatch(setVerificationType({ type: SMS_AND_BANKID }))
      }

      if (content?.error === 'bankid_verification_needed') {
        return dispatch(setVerificationType({ type: BANKID }))
      }

      if (content?.error === 'sms_verification_needed') {
        return dispatch(setVerificationType({ type: SMS }))
      }
    }

    // try to re-fetch connect properties one time
    if (retries < maxRetries) {
      await dispatch(fetchConnectProperties(true))
      return dispatch(createChannel(adId, retries + 1))
    }

    dispatch(
      errorHandler({
        error,
        errorType: 'general',
        sentryLog: { description: `Error response from: ${endpoint}` },
      }),
    )
    trackError(error, adId)
  }
}

export const recipientBankIdVerifiedCheck = (channels) => async (dispatch, getState) => {
  const { checkedRecipientBankIdVerifiedChannels, sendbirdUser } = getState().chat

  const filterChannels = (channels, checkedRecipientBankIdVerifiedChannels) => {
    return channels
      .filter(
        (channel) =>
          !checkedRecipientBankIdVerifiedChannels.some(
            (checkedChannel) => channel.url === checkedChannel.url,
          ),
      )
      .map((uncheckedChannel) => uncheckedChannel.url)
  }

  const formatCheckedRecipientBankIdVerifiedChannels = (
    responseChannels,
    currentUserSendBirdId,
  ) => {
    return responseChannels.map((matchingChannel) => ({
      url: matchingChannel.channelUrl,
      recipientBankIdVerified: matchingChannel.members
        ?.filter((member) => member.sendbirdAccountId !== currentUserSendBirdId)
        .some((recipient) => recipient?.bankIdVerified),
    }))
  }

  const fetchVerifiedMembers = async () => {
    const endpoint = 'messaging-sendbird-communication/v1/channels/info'
    const { suspendedAccount: suspendedAccountFeature } = getState().optimizely.features
    try {
      const uncheckedChannelUrls = filterChannels(channels, checkedRecipientBankIdVerifiedChannels)

      if (uncheckedChannelUrls.length) {
        const response = await dispatch(
          api.fetch(endpoint, {
            method: 'POST',
            body: JSON.stringify({ channel_urls: uncheckedChannelUrls }),
          }),
        )
        dispatch(
          addRecipientMemberBankidVerifiedChannelUrl(
            formatCheckedRecipientBankIdVerifiedChannels(response.channels, sendbirdUser.userId),
          ),
        )
      }
    } catch (error) {
      const { content, response } = error

      if (
        suspendedAccountFeature?.enabled &&
        response?.status === 403 &&
        content?.error === '400301_account_suspended'
      ) {
        log.message(`SuspendedUser error: ${error}. Endpoint: ${endpoint}`)
        return dispatch(setUserSuspendedType(content.error))
      }

      log.message(`fetchVerifiedMembers error: ${error}`)
    }
  }
  fetchVerifiedMembers()
}
